import React from 'react';
import Box from '../../../components/Common/Box';
import PremiumLock from '../../../components/Common/PremiumLock';
import PortfolioEvolutionChart from '../../../components/Charts/PortfolioEvolutionChart';
import { useAuth } from '../../../context/AuthContext';
import { AlerteComportementale, useBehavioralAlerts } from '../useBehavioralAlerts';
import { PortfolioAnalysisData } from '../usePortfolioAnalysis';
import { couleur, dateLongue, pctSigne } from '../portfolioFormat';

/* Briques partagées par les écrans desktop et mobile de l'analyse du portefeuille. */

export const titreSection: React.CSSProperties = {
  margin: 0,
  fontSize: '17px',
  fontWeight: 700,
  color: 'var(--text-heading)',
};

export const note: React.CSSProperties = {
  fontSize: '12px',
  color: 'var(--text-muted)',
  lineHeight: 1.5,
};

interface KpiCardProps {
  libelle: string;
  valeur: string;
  couleurValeur?: string;
  detail?: React.ReactNode;
  aide?: string;
  compact?: boolean;
}

/** Carte d'indicateur : libellé, valeur, ligne de détail. `aide` = infobulle de définition. */
export const KpiCard: React.FC<KpiCardProps> = ({
  libelle,
  valeur,
  couleurValeur,
  detail,
  aide,
  compact,
}) => (
  <Box>
    <div style={{ padding: compact ? '14px' : '18px 20px' }} title={aide}>
      <div
        style={{
          fontSize: compact ? '12px' : '13px',
          color: 'var(--text-muted)',
          marginBottom: '6px',
        }}
      >
        {libelle}
        {aide && (
          <span style={{ marginLeft: '4px', cursor: 'help' }} aria-label={aide}>
            ⓘ
          </span>
        )}
      </div>
      <div
        style={{
          fontSize: compact ? '18px' : '22px',
          fontWeight: 700,
          color: couleurValeur ?? 'var(--text-heading)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {valeur}
      </div>
      {detail && <div style={{ ...note, marginTop: '4px' }}>{detail}</div>}
    </div>
  </Box>
);

/** Performances jour / semaine / mois / depuis le 1er janvier, en une seule carte. */
export const PeriodesCard: React.FC<{ data: PortfolioAnalysisData; compact?: boolean }> = ({
  data,
  compact,
}) => {
  const periodes = [
    { libelle: 'Jour', pct: data.total_pl_day_pct },
    { libelle: 'Semaine', pct: data.total_pl_week_pct },
    { libelle: 'Mois', pct: data.total_pl_month_pct },
    { libelle: 'Depuis le 1er janv.', pct: data.total_pl_ytd_pct },
  ];
  return (
    <Box>
      <div
        style={{
          padding: compact ? '12px' : '16px 20px',
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '8px',
        }}
        title='Variation de la valeur des titres, achats et ventes de la période neutralisés.'
      >
        {periodes.map((p) => (
          <div key={p.libelle} style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px' }}>
              {p.libelle}
            </div>
            <div
              style={{
                fontSize: compact ? '13px' : '16px',
                fontWeight: 700,
                color: couleur(p.pct),
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {pctSigne(p.pct)}
            </div>
          </div>
        ))}
      </div>
    </Box>
  );
};

/**
 * Évolution du portefeuille. Par défaut la performance HORS APPORTS : la valeur des titres
 * monte à chaque achat, sa courbe montre donc les versements autant que le marché.
 */
export const EvolutionCard: React.FC<{ data: PortfolioAnalysisData; hauteur: number }> = ({
  data,
  hauteur,
}) => {
  const perfDisponible = data.performance_history.length > 1;
  const [vue, setVue] = React.useState<'perf' | 'valeur'>(perfDisponible ? 'perf' : 'valeur');
  const serie = vue === 'perf' ? data.performance_history : data.portfolio_history;

  const bouton = (cle: 'perf' | 'valeur', libelle: string) => (
    <button
      type='button'
      onClick={() => setVue(cle)}
      disabled={cle === 'perf' && !perfDisponible}
      style={{
        padding: '6px 12px',
        borderRadius: '8px',
        border: 'none',
        fontSize: '13px',
        fontWeight: 600,
        cursor: 'pointer',
        background: vue === cle ? '#2563eb' : 'transparent',
        color: vue === cle ? '#ffffff' : 'var(--text-muted)',
      }}
    >
      {libelle}
    </button>
  );

  return (
    <Box>
      <div style={{ padding: '20px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '10px',
            marginBottom: '6px',
          }}
        >
          <h3 style={titreSection}>
            Évolution du portefeuille
            {data.history_days_available > 0 && (
              <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: '13px' }}>
                {' '}
                · {data.history_days_available} dernières séances
              </span>
            )}
          </h3>
          <div
            style={{
              display: 'flex',
              background: 'var(--bg-hover)',
              borderRadius: '10px',
              padding: '3px',
            }}
          >
            {bouton('perf', 'Performance')}
            {bouton('valeur', 'Valeur des titres')}
          </div>
        </div>
        <p style={{ ...note, margin: '0 0 14px 0' }}>
          {vue === 'perf'
            ? "Performance cumulée hors apports : vos achats et ventes sont neutralisés, la courbe ne montre que l'effet du marché sur vos titres (dividendes non inclus)."
            : 'Valeur de marché de vos titres, liquidités non comprises : chaque achat la fait monter, chaque vente la fait baisser.'}
        </p>
        <div style={{ height: `${hauteur}px`, width: '100%' }}>
          {serie.length > 0 ? (
            <PortfolioEvolutionChart
              data={serie}
              height={hauteur}
              format={vue === 'perf' ? 'pct' : 'fcfa'}
            />
          ) : (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                ...note,
                textAlign: 'center',
              }}
            >
              Historique en cours de constitution — revenez dans quelques jours.
            </div>
          )}
        </div>
      </div>
    </Box>
  );
};

const COULEUR_SEVERITE: Record<string, string> = {
  HIGH: '#ef4444',
  MEDIUM: '#f59e0b',
  LOW: '#64748b',
};

const BlocTexte: React.FC<{ titre: string; texte: string }> = ({ titre, texte }) => (
  <div>
    <div
      style={{
        fontSize: '11px',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        color: 'var(--text-muted)',
        marginBottom: '3px',
      }}
    >
      {titre}
    </div>
    <div style={{ fontSize: '13px', color: 'var(--text-main)', lineHeight: 1.55 }}>{texte}</div>
  </div>
);

/** Une alerte : ce qui a été constaté, pourquoi c'est un risque, ce que le client peut faire. */
const CarteAlerte: React.FC<{ alerte: AlerteComportementale }> = ({ alerte }) => {
  const teinte = COULEUR_SEVERITE[alerte.severite ?? 'MEDIUM'] ?? COULEUR_SEVERITE.MEDIUM;
  return (
    <div
      style={{
        padding: '16px',
        borderRadius: '10px',
        border: '1px solid var(--border-color)',
        borderLeft: `4px solid ${teinte}`,
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      }}
    >
      <div
        style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}
      >
        <div>
          <div style={{ fontWeight: 700, fontSize: '15px', color: 'var(--text-heading)' }}>
            {alerte.emoji} {alerte.titre}
            {alerte.symbole && <span style={{ color: '#2563eb' }}> · {alerte.symbole}</span>}
          </div>
          <div style={{ ...note, fontSize: '11px', marginTop: '2px' }}>
            {alerte.nom_societe ? `${alerte.nom_societe} · ` : ''}détecté le{' '}
            {dateLongue(alerte.date_signal)}
          </div>
        </div>
        <span
          style={{
            alignSelf: 'flex-start',
            fontSize: '11px',
            fontWeight: 700,
            color: teinte,
            border: `1px solid ${teinte}`,
            borderRadius: '999px',
            padding: '2px 8px',
            whiteSpace: 'nowrap',
          }}
        >
          Vigilance {alerte.severite_libelle.toLowerCase()}
        </span>
      </div>
      {alerte.constat && <BlocTexte titre='Ce que nous avons constaté' texte={alerte.constat} />}
      <BlocTexte titre="Pourquoi c'est un risque" texte={alerte.explication} />
      <BlocTexte titre='Ce que vous pouvez faire' texte={alerte.recommandation} />
    </div>
  );
};

const ListeAlertes: React.FC = () => {
  const { alertes, loading, error } = useBehavioralAlerts(true);
  if (loading) return <div style={note}>Chargement des alertes…</div>;
  if (error) return <div style={note}>{error}</div>;
  if (alertes.length === 0) {
    return (
      <div style={note}>Aucun comportement à risque détecté récemment sur votre portefeuille.</div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {alertes.map((a) => (
        <CarteAlerte key={a.id} alerte={a} />
      ))}
    </div>
  );
};

/** Alertes comportementales (Pro). Les alertes ne sont chargées que pour les comptes autorisés. */
export const AlertesComportementales: React.FC = () => {
  const { hasPermission } = useAuth();
  const autorise = hasPermission('alertes_pro') || hasPermission('admin_platform');
  return (
    <Box>
      <div style={{ padding: '20px' }}>
        <h3 style={{ ...titreSection, marginBottom: '16px' }}>Alertes comportementales</h3>
        {autorise ? (
          <ListeAlertes />
        ) : (
          <PremiumLock
            title='Alertes comportementales'
            description="Détectez automatiquement les erreurs classiques d'investisseur (moyenne à la baisse répétée, sur-concentration, achat après forte hausse, trading excessif) dans votre propre historique, avec une recommandation pour chacune. Disponible avec le plan Kanea Pro."
          />
        )}
      </div>
    </Box>
  );
};
